import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { Button } from '@/components/ui/button';
import { AlertCircle, Clock, CheckCircle, MessageCircle } from 'lucide-react';
import { useAdminConfig } from '@/hooks/useAdminConfig';
import { useToast } from '@/hooks/use-toast';

interface DisputeCase {
  id: string;
  type: 'customer' | 'provider';
  subject: string;
  category: string;
  priority: 'URGENT' | 'HIGH' | 'NORMAL';
  status: 'AWAITING ACTION' | 'IN REVIEW' | 'UNASSIGNED' | 'ESCALATED' | 'RESOLVED' | 'CLOSED';
  assignedTo: { name: string; initials: string; avatar?: string };
  lastActivity: string;
}

const AdminSupportDisputesPage = () => {
  const { config } = useAdminConfig();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('open');
  const navigate = useNavigate();
  const [casesData, setCasesData] = useState<DisputeCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [avgResponseTime, setAvgResponseTime] = useState<string>('N/A');

  // Fetch dispute cases from database
  useEffect(() => {
    const fetchCases = async () => {
      try {
        const response = await apiService.request('/admin/disputes', 'GET');
        if (response.success && Array.isArray(response.data)) {
          const avgFromApi = response.meta?.avg_response_time ?? response.data_meta?.avg_response_time;
          if (avgFromApi) {
            setAvgResponseTime(String(avgFromApi));
          } else {
            const resolved = response.data.filter((d: any) => d.first_response_at && d.created_at);
            if (resolved.length > 0) {
              const totalHrs = resolved.reduce((sum: number, d: any) => {
                return sum + (new Date(d.first_response_at).getTime() - new Date(d.created_at).getTime()) / 3600000;
              }, 0);
              const avg = totalHrs / resolved.length;
              setAvgResponseTime(avg < 1 ? `${Math.round(avg * 60)}m` : `${avg.toFixed(1)}h`);
            }
          }
          const mapped: DisputeCase[] = response.data.map((d: any) => {
            const assigneeName = d.assigned_to_name || d.assignedTo?.name || d.assigned_to || 'Unassigned';
            const initials = assigneeName.split(' ').map((p: string) => p[0]).join('').slice(0, 2).toUpperCase() || 'U';
            return {
              id: d.case_number || d.id ? `#DIS-${String(d.id).padStart(5, '0')}` : '—',
              type: (d.complainant_type || d.type || 'customer') as 'customer' | 'provider',
              subject: d.subject || d.title || d.description?.slice(0, 50) || '—',
              category: d.category || d.dispute_type || '—',
              priority: (['URGENT', 'HIGH', 'NORMAL'].includes((d.priority || '').toUpperCase())
                ? d.priority.toUpperCase()
                : 'NORMAL') as DisputeCase['priority'],
              status: (['AWAITING ACTION', 'IN REVIEW', 'UNASSIGNED', 'ESCALATED', 'RESOLVED', 'CLOSED'].includes((d.status || '').toUpperCase())
                ? d.status.toUpperCase()
                : 'UNASSIGNED') as DisputeCase['status'],
              assignedTo: { name: assigneeName, initials },
              lastActivity: d.updated_at
                ? new Date(d.updated_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
                : d.lastActivity || '—'
            };
          });
          setCasesData(mapped);
        }
      } catch (error) {
        console.error('Failed to fetch dispute cases:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, []);

  // Handler: Resolve dispute case
  const handleResolveCase = async (caseId: string) => {
    try {
      const response = await apiService.request(`/admin/disputes/${caseId}/resolve`, 'POST', {
        status: 'RESOLVED',
        resolution: 'Resolved by admin',
        resolvedBy: localStorage.getItem('adminId'),
        resolvedAt: new Date().toISOString()
      });
      if (response.success) {
        toast({ title: config.messages.caseResolvedSuccess });
        window.location.reload();
      } else {
        toast({ title: 'Error: ' + response.message });
      }
    } catch (error: any) {
      console.error('Resolve case error:', error);
      toast({ title: 'Failed to resolve case: ' + error.message });
    }
  };

  // Handler: View dispute details
  const handleViewCase = (caseId: string) => {
    navigate(`/admin/support-disputes/${caseId}`);
  };

  // Handler: Assign to operator
  const handleAssignCase = async (caseId: string, operatorId: string) => {
    try {
      const response = await apiService.request(`/admin/disputes/${caseId}/assign`, 'PATCH', {
        assignedTo: operatorId,
        assignedBy: localStorage.getItem('adminId'),
        assignedAt: new Date().toISOString()
      });
      if (response.success) {
        toast({ title: config.messages.caseAssignedSuccess });
        window.location.reload();
      } else {
        toast({ title: 'Error: ' + response.message });
      }
    } catch (error: any) {
      console.error('Assign case error:', error);
      toast({ title: 'Failed to assign case: ' + error.message });
    }
  };

  const openCount = casesData.filter(c => !['RESOLVED', 'CLOSED', 'ESCALATED'].includes(c.status)).length;
  const urgentCount = casesData.filter(c => c.priority === 'URGENT').length;
  const resolvedCount = casesData.filter(c => c.status === 'RESOLVED' || c.status === 'CLOSED').length;

  const metrics = [
    {
      label: 'OPEN CASES',
      value: String(openCount),
      trend: `${casesData.length} total`,
      description: 'Active queue excluding escalated',
      color: 'orange'
    },
    {
      label: 'URGENT',
      value: String(urgentCount),
      trend: `Requires response < ${config.disputes.urgentResponseWindowHours}hrs`,
      description: 'High priority cases',
      color: 'red'
    },
    {
      label: 'AVG RESPONSE',
      value: avgResponseTime,
      trend: 'Live data',
      description: `Performance vs last ${config.disputes.performanceWindowDays} days`,
      color: 'gray'
    },
    {
      label: 'RESOLVED',
      value: String(resolvedCount),
      trend: 'From all time',
      description: 'Cases closed',
      color: 'green'
    }
  ];

  const tabs = [
    { id: 'open', label: 'Open', count: casesData.filter(c => ['AWAITING ACTION', 'IN REVIEW', 'UNASSIGNED'].includes(c.status)).length },
    { id: 'awaiting', label: 'Awaiting Response', count: casesData.filter(c => c.status === 'AWAITING ACTION').length },
    { id: 'progress', label: 'In Progress', count: casesData.filter(c => c.status === 'IN REVIEW').length },
    { id: 'escalated', label: 'Escalated', count: casesData.filter(c => c.status === 'ESCALATED').length }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'text-red-700 bg-red-50 border border-red-200';
      case 'HIGH':
        return 'text-orange-700 bg-orange-50 border border-orange-200';
      case 'NORMAL':
        return 'text-green-700 bg-green-50 border border-green-200';
      default:
        return 'text-gray-700 bg-gray-50 border border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'AWAITING ACTION':
        return 'text-orange-700 bg-orange-50 border border-orange-200';
      case 'IN REVIEW':
        return 'text-green-700 bg-green-50 border border-green-200';
      case 'ESCALATED':
        return 'text-red-700 bg-red-50 border border-red-200';
      case 'UNASSIGNED':
        return 'text-gray-700 bg-gray-50 border border-gray-200';
      default:
        return 'text-gray-700 bg-gray-50 border border-gray-200';
    }
  };

  const getMetricIcon = (color: string) => {
    switch (color) {
      case 'orange':
        return <div className="w-3 h-3 bg-orange-500 rounded-full"></div>;
      case 'red':
        return <div className="w-3 h-3 bg-red-600 rounded-full"></div>;
      case 'green':
        return <div className="w-3 h-3 bg-green-600 rounded-full"></div>;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  return (
    <>
      <Helmet>
        <title>Support & Disputes - Virewire</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 p-8 min-h-screen">
          {/* Breadcrumb */}
          <div className="text-sm text-gray-600 mb-8">
            <span>Operations</span>
            <span className="mx-2">›</span>
            <span className="text-gray-900 font-semibold">Dashboard</span>
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-4 gap-6 mb-8">
            {metrics.map((metric, idx) => (
              <div key={idx} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <p className="text-xs uppercase font-bold text-gray-600">{metric.label}</p>
                  {getMetricIcon(metric.color)}
                </div>
                <p className="text-4xl font-bold text-gray-900 mb-2">{metric.value}</p>
                <p className={`text-sm font-semibold mb-2 ${
                  metric.color === 'red'
                    ? 'text-red-600'
                    : metric.color === 'orange'
                    ? 'text-orange-600'
                    : metric.color === 'green'
                    ? 'text-green-600'
                    : 'text-gray-600'
                }`}>
                  {metric.trend}
                </p>
                <p className="text-xs text-gray-600">{metric.description}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="flex border-b border-gray-200">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-teal-600 text-teal-600 bg-gray-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                  <span className="ml-2 font-bold">({tab.count})</span>
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-600 uppercase">Case #</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-600 uppercase">Type</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-600 uppercase">Subject</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-600 uppercase">Priority</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-600 uppercase">Status</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-600 uppercase">Assigned To</th>
                    <th className="text-left px-6 py-4 text-xs font-bold text-gray-600 uppercase">Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-500">Loading disputes...</td></tr>
                  )}
                  {!loading && casesData.length === 0 && (
                    <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-500">No dispute cases found.</td></tr>
                  )}
                  {casesData.map((caseItem, idx) => (
                    <tr
                      key={idx}
                      onClick={() => navigate(`/admin/support-disputes/${caseItem.id.replace('#', '')}`)}
                      className="border-b border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <span className="text-sm font-bold text-teal-600">{caseItem.id}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          {caseItem.type === 'customer' ? (
                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-xs">👤</span>
                            </div>
                          ) : (
                            <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                              <span className="text-xs">🏢</span>
                            </div>
                          )}
                          <span className="capitalize">{caseItem.type}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{caseItem.subject}</p>
                          <p className="text-xs text-gray-600">{caseItem.category}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex text-xs font-bold px-3 py-1 rounded-full border ${getPriorityColor(caseItem.priority)}`}>
                          {caseItem.priority === 'URGENT' && '●'} {caseItem.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex text-xs font-bold px-3 py-1 rounded-full border ${getStatusColor(caseItem.status)}`}>
                          {caseItem.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            {caseItem.assignedTo.initials[0]}
                          </div>
                          <span className="text-sm font-medium text-gray-900">{caseItem.assignedTo.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">{caseItem.lastActivity}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
              <span className="text-sm text-gray-600">Showing {casesData.length} results</span>
              <div className="flex items-center gap-2">
                <button className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-100 transition-colors">
                  ‹
                </button>
                <button className="px-3 py-1 bg-teal-600 text-white rounded font-bold text-sm">
                  1
                </button>
                <button className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm">
                  2
                </button>
                <button className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 transition-colors text-sm">
                  3
                </button>
                <button className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-100 transition-colors">
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminSupportDisputesPage;
